import { useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useWheelNavigation } from '../hooks/useWheelNavigation';

export const SelectCompanyView = ({ handleItemSelect, selectedIndex, setSelectedIndex, mode = 'select' }: any) => {
  const scrollRef = useRef(null);
  const setActiveCompany = useStore((state) => state.setActiveCompany);
  const activeCompany = useStore((state) => state.activeCompany);
  const setCurrentAlterVoucherId = useStore((state) => state.setCurrentAlterVoucherId);
  const setBinboxData = useStore((state) => state.setBinboxData);
  const [isSwitching, setIsSwitching] = useState(false);

  const companies = useLiveQuery(async () => {
    const comps = await db.companies.toArray();
    const compsWithDates = await Promise.all(comps.map(async (comp) => {
      const vouchers = await db.vouchers.where('companyId').equals(comp.id!).filter(v => !v.deleted).toArray();
      let lastEntryDate = '-';
      
      if (vouchers && vouchers.length > 0) {
        let maxDate = vouchers[0].date;
        for (let i = 1; i < vouchers.length; i++) {
          if (vouchers[i].date > maxDate) {
            maxDate = vouchers[i].date;
          }
        }
        
        const d = new Date(maxDate);
        if (!isNaN(d.getTime())) {
          const day = d.getDate().toString().padStart(2, '0');
          const month = (d.getMonth() + 1).toString().padStart(2, '0');
          const year = d.getFullYear();
          lastEntryDate = `${day}-${month}-${year}`;
        }
      }
      return { ...comp, lastEntryDisplay: lastEntryDate };
    }));
    return compsWithDates;
  });

  const flatItems = mode === 'select' ? [
    { name: 'Create Company', isAction: true, action: 'create' },
    ...(companies || []).map(c => ({ ...c, isAction: false })),
    { name: 'Quit', isAction: true, action: 'quit' }
  ] : [
    ...(companies || []).map(c => ({ ...c, isAction: false })),
    { name: 'Quit', isAction: true, action: 'quit' }
  ];

  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => {
    if (!flatItems || !flatItems[selectedIndex]) return;
    const item = flatItems[selectedIndex] as any;
    if (item.isAction) {
      if (item.action === 'quit') {
        handleItemSelect({ name: 'Quit' });
      } else {
        handleItemSelect(item, selectedIndex);
      }
    } else {
      let nextLevel = '';
      if (mode === 'alter') nextLevel = 'alter_company_form';
      else if (mode === 'delete') nextLevel = 'delete_company_confirm';
      else if (mode === 'backup') nextLevel = 'backup_company_process';

      if (nextLevel) {
        handleItemSelect({ name: item.name, isAction: false, id: item.id, level: nextLevel }, selectedIndex);
      } else {
        handleItemSelect({ name: item.name, isAction: false, id: item.id }, selectedIndex);
      }
    }
  });

  const onSelect = async (item: any) => {
    if (item.isAction) {
      if (item.action === 'create') {
        handleItemSelect({ name: 'Create Company', level: 'creation_company', id: null });
      } else if (item.action === 'quit') {
        handleItemSelect({ name: 'Quit' });
      }
    } else {
      if (mode === 'select') {
        // Prevent double-click switching
        if (isSwitching) return;
        setIsSwitching(true);

        try {
          // ── Step 1: Clear all stale company-specific state before switching ──
          setActiveCompany(null);
          setCurrentAlterVoucherId(null);
          setBinboxData(null);

          // ── Step 2: Small yield so React re-renders with null company
          //    (useDashboardStats will bail out early — no stale queries) ──
          await new Promise(resolve => setTimeout(resolve, 0));

          // ── Step 3: Update DB — deactivate all, activate selected ──
          await db.companies.where('isActive').equals(1).modify({ isActive: 0 });
          await db.companies.update(item.id, { isActive: 1 });

          // ── Step 4: Fetch fresh company record from DB (not stale list item) ──
          const freshCompany = await db.companies.get(item.id);
          if (!freshCompany) throw new Error('Company not found after activation');

          // ── Step 5: Set new active company (triggers dashboard lazy reload) ──
          setActiveCompany({ ...freshCompany, isActive: 1 });

          handleItemSelect({ name: 'Quit' });
        } catch (err) {
          console.error('[SelectCompany] Switch failed:', err);
          setIsSwitching(false);
        }
      } else if (mode === 'alter') {
        handleItemSelect({ name: item.name, id: item.id, level: 'alter_company_form' });
      } else if (mode === 'delete') {
        handleItemSelect({ name: item.name, id: item.id, level: 'delete_company_confirm' });
      } else if (mode === 'backup') {
        handleItemSelect({ name: item.name, id: item.id, level: 'backup_company_process' });
      }
    }
  };

  const titles: any = {
    select: 'Select Company',
    alter: 'Alter Company',
    delete: 'Delete Company',
    backup: 'Backup Company',
    restore: 'Restore Company'
  };

  return (
    <div className="flex flex-col w-full h-full bg-[#f8faf7] z-50 absolute inset-0 font-sans text-[13px] text-black border-2 border-[#1e8585]">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
        <span>{titles[mode] || 'Select Company'}</span>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden p-4">
        <div className="flex border-b border-gray-400 font-bold px-2 py-1 bg-[#d9e4d5]">
          <span className="flex-[2]">List of Companies</span>
          <span className="flex-1 text-right">Date of Last Entry</span>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white border border-gray-300">
          {isSwitching && (
            <div className="flex items-center justify-center py-3 text-[#1e8585] font-bold text-[12px] bg-[#e8f4f4] border-b border-[#1e8585]">
              <span className="animate-pulse">⟳ Switching company...</span>
            </div>
          )}
          {flatItems.map((item: any, idx: number) => (
            <div 
              key={idx}
              className={`flex px-2 py-1 cursor-pointer ${isSwitching ? 'opacity-50 pointer-events-none' : ''} ${selectedIndex === idx ? 'bg-[#1e8585] text-white active-row font-bold' : ((!item.isAction && item.id === activeCompany?.id) ? 'font-bold text-[#1e8585]' : 'text-black')}`}
              onClick={() => onSelect(item)}
            >
              <div className="flex-[2] flex items-center">
                <span>{item.name}</span>
                {!item.isAction && item.id === activeCompany?.id && <span className="ml-2 text-[10px] italic">(Active)</span>}
              </div>
              <div className="flex-1 text-right tabular-nums">
                {!item.isAction ? item.lastEntryDisplay : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-[#1e8585] text-white text-[11px] px-2 py-1 flex justify-between">
        <span>Enter: Select | Esc: Quit</span>
      </div>
    </div>
  );
};
